import Vue from 'vue';
import Vuex from 'vuex';
import VueRouter from 'vue-router';
import { sync } from 'vuex-router-sync';

import env from '#shared/config/env.js';

import appError from './appError.js';
import meta from './vue.meta.js';
import getLink from './vue.getLink.js';
import waitUntil from './vue.waitUntil.js';
import updateUrlQuery from './vue.updateUrlQuery.js';
import retargetingToaster from './vue.retargetingToaster.js';
import closeWindowOffer from './vue.closeWindowOffer.js';

// Enable global error handler
import './uncaughtErrorHandler.js';

const activateProducts = IS_CLIENT && await import('#frontend/util/vue.activateProducts.js');
const cookieStorage = IS_CLIENT && await import('#frontend/util/vue.cookieStorage.js');
const isEmailDomain = IS_CLIENT && await import('#frontend/util/vue.isEmailDomain.js');
const loginByTicket = IS_CLIENT && await import('#frontend/util/vue.loginByTicket.js');
const getAccountFromClient = IS_CLIENT && await import('#frontend/util/vue.getAccountFromClient.js');
const rememberForm = IS_CLIENT && await import('#frontend/util/vue.rememberForm.js');
const sessionStorage = IS_CLIENT && await import('#frontend/util/vue.sessionStorage.js');
const handleMultiple = IS_CLIENT && await import('#frontend/util/vue.handleMultiple.js');
const tracking = IS_CLIENT && await import('#frontend/util/vue.tracking.js');
const validate = IS_CLIENT && await import('#frontend/util/vue.validate.js');
const providersLibrary = IS_CLIENT && await import('#frontend/util/vue.providersLibrary.js');
const websocket = IS_CLIENT && await import('#frontend/util/vue.websocket.js');

// Enable Vuex
Vue.use(Vuex);

// Enable Vue router
Vue.use(VueRouter);

// Enable custom link generation function
Vue.use(getLink);

// Enable Promise based "wait for state" function
Vue.use(waitUntil);

// Enable url query unifier and modifier function
Vue.use(updateUrlQuery);

// Enable custom meta package
Vue.use(meta);

// Attach retargeting toaster module
Vue.use(retargetingToaster);

// Attach close window offer module
Vue.use(closeWindowOffer);

if (IS_CLIENT) {
	// Attach product activations module
	Vue.use(activateProducts);

	// Enable custom cookie storage plugin
	Vue.use(cookieStorage);

	// Attach email domain validation
	Vue.use(isEmailDomain);

	// Attach login by ticket module
	Vue.use(loginByTicket);

	// Attach account from client fetcher module
	Vue.use(getAccountFromClient);

	// Enable custom form remember/restore plugin
	Vue.use(rememberForm);

	// Enable custom session storage plugin
	Vue.use(sessionStorage);

	// Enable chained async events handler plugin
	Vue.use(handleMultiple);

	// Attach tracking module
	Vue.use(tracking);

	// Extend all Vue components with mixin
	Vue.use(validate);
}

// Extend all Vue components with translation method and customIDs
Vue.mixin({
	computed: Vuex.mapGetters({
		t: 'getLocalization',
		isCustomId: 'isCustomId'
	})
});

// Extend all Vue components with v-focus directive
Vue.directive('focus', {
	inserted(el) {
		el.focus();
	}
});

// Add global Vue error handler
if (!env.isLocalhost) {
	Vue.config.errorHandler = (error) => {
		appError(error, {
			code: 'VUE',
			origin: 'createTheme'
		});
	};
}

if (IS_SERVER) {
	// Add global provider's library (HPP.js) object (required for correct HTML rendering)
	Vue.prototype.providersLibrary = {};
}

// Disable production tip in console
Vue.config.productionTip = false;

// Force-enable devtools which enables to browse and modify Vue data
Vue.config.devtools = true;

export default (createOptions = {}) => {
	const options = Object.assign({}, {
		routes: [],
		store: {}
	}, createOptions);

	// Add theme-specific router also with an error routes
	const themeRoutes = options.routes.map((route) => Object.assign({}, route, {
		path: `/:locale/${options.theme}/${route.path}`
	}));
	const errorRoutes = [{
		path: `/:locale/${options.theme}/*`,
		name: 'error-action',
		component: options.errorComponent
	}, {
		path: '/:locale/*',
		name: 'error-theme',
		component: options.errorComponent
	}, {
		path: '/:locale*',
		name: 'error-locale',
		component: options.errorComponent
	}, {
		path: '*',
		name: 'error',
		component: options.errorComponent
	}];

	// Init the vue router
	const router = new VueRouter({
		mode: 'history',
		routes: [
			...themeRoutes,
			...errorRoutes
		]
	});

	// When client router hits error route, load it from server instead
	router.beforeEach((to, from, next) => {
		if (IS_CLIENT && from.name && to.name.startsWith('error')) {
			location.href = to.path;
		} else {
			next();
		}
	});

	// Init the store
	const store = new Vuex.Store(options.store);

	// Sync the store and a router
	sync(store, router);

	if (IS_CLIENT) {
		// Attach provider's library (HPP.js)
		Vue.use(providersLibrary, {
			store
		});

		// Enable WebSocket client
		Vue.use(websocket, {
			store
		});
	}

	// Init the application
	const app = new Vue({
		router,
		store,
		render: (createElement) => createElement(options.parentComponent)
	});

	return {
		app,
		router,
		store,
		options
	};
};
